/**
 * ScrollToTop — luxury floating "jump to top" button.
 *
 * - Appears once the page is scrolled past 400px.
 * - Smooth-scrolls back to the top of the document.
 * - Brand styled: navy circle with gold ring + chevron, subtle entrance fade.
 * - Mounted globally in App.js so every page gets it for free.
 */
import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";

export default function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // initial sync (e.g. when arriving deep-linked)
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Back to top"
      data-testid="scroll-to-top"
      className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full
        bg-tbc-navy text-tbc-gold shadow-lg shadow-tbc-navy/30
        ring-2 ring-tbc-gold/40 hover:ring-tbc-gold
        hover:bg-tbc-navy/95 active:scale-95
        transition-all duration-300 ease-out
        flex items-center justify-center
        ${visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-3 pointer-events-none"}
      `}
    >
      <ArrowUp className="w-5 h-5" strokeWidth={2.5} />
    </button>
  );
}
